import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import Api from 'services/api';
import ToastApi from 'services/toast';

import PageTitleAndDescription from 'components/pageTitleAndDescription';
import { Col, Row } from 'components/grid';
import Loader from 'components/loader';

import Panel from './panel';

import {
  ButtonSave,
  ColSave,
  StyledCol,
  Subtitle,
  PageWrapper,
} from './styles';

const GlobalFiltersPage = () => {
  const { t } = useTranslation();

  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingSave, setLoadingSave] = useState(false);

  const idRef = useRef('');
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [productLines, setProductLines] = useState([]);
  const [showBrands, setShowBrands] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [showMarkets, setShowMarkets] = useState(false);
  const [showProductLines, setShowProductLines] = useState(false);
  const [showVehicleLines, setShowVehicleLines] = useState(false);
  const [showVehicleTypes, setShowVehicleTypes] = useState(false);
  const [vehicleLines, setVehicleLines] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);

  useEffect(() => {
    const asyncInitFunction = async () => {
      try {
        const data = await Api.loadGlobalFilters();

        idRef.current = data.id;
        setBrands(data.brands.items);
        setCategories(data.categories.items);
        setCompanies(data.companies.items);
        setMarkets(data.markets.items);
        setProductLines(data.productLines.items);
        setShowBrands(data.brands.showInCatalog);
        setShowCategories(data.categories.showInCatalog);
        setShowMarkets(data.markets.showInCatalog);
        setShowProductLines(data.productLines.showInCatalog);
        setShowVehicleLines(data.vehicleLines.showInCatalog);
        setShowVehicleTypes(data.vehicleTypes.showInCatalog);
        setVehicleLines(data.vehicleLines.items);
        setVehicleTypes(data.vehicleTypes.items);
        setLoadingPage(false);
      } catch (error) {
        ToastApi.error(error.message);
      }
    };
    asyncInitFunction();
  }, []);

  const handleSave = async () => {
    try {
      const request = {
        id: idRef.current,
        brands: {
          items: brands,
          showInCatalog: showBrands,
        },
        categories: {
          items: categories,
          showInCatalog: showCategories,
        },
        companies: {
          items: companies,
        },
        markets: {
          items: markets,
          showInCatalog: showMarkets,
        },
        productLines: {
          items: productLines,
          showInCatalog: showProductLines,
        },
        vehicleLines: {
          items: vehicleLines,
          showInCatalog: showVehicleLines,
        },
        vehicleTypes: {
          items: vehicleTypes,
          showInCatalog: showVehicleTypes,
        },
      };
      setLoadingSave(true);
      await Api.saveGlobalFilters(request);
      ToastApi.success(t('common.saveSuccess'));
    } catch (error) {
      ToastApi.error(error.message);
    } finally {
      setLoadingSave(false);
    }
  };

  const handleOnChangeItems = (items, setFunc, beforeItems) => {
    if (items.find((item) => item.selected)) {
      setFunc(items);
    } else {
      setFunc([...beforeItems]);
      ToastApi.error(t('pages.globalFiltersPage.errorSelectOne'));
    }
  };

  const renderContent = () => (
    <Row>
      <Col>
        <Subtitle>{t('pages.globalFiltersPage.subtitleVehicle')}</Subtitle>
      </Col>
      <Col>
        <Row>
          <StyledCol col={6} xl={4}>
            <Panel
              items={vehicleLines}
              onChangeItems={(items) => handleOnChangeItems(items, setVehicleLines, vehicleLines)}
              onChangeShowInCatalog={(show) => setShowVehicleLines(show)}
              showInCatalog={showVehicleLines}
              title={t('pages.globalFiltersPage.vehicleLine')}
              titleSelectAll={t('pages.globalFiltersPage.selectVehicleLine')}
            />
          </StyledCol>
          <StyledCol col={6} xl={4}>
            <Panel
              items={vehicleTypes}
              onChangeItems={(items) => handleOnChangeItems(items, setVehicleTypes, vehicleTypes)}
              onChangeShowInCatalog={(show) => setShowVehicleTypes(show)}
              showInCatalog={showVehicleTypes}
              title={t('pages.globalFiltersPage.vehicleType')}
              titleSelectAll={t('pages.globalFiltersPage.selectVehicleTypes')}
            />
          </StyledCol>
          <StyledCol col={6} xl={4}>
            <Panel
              items={brands}
              onChangeItems={(items) => handleOnChangeItems(items, setBrands, brands)}
              onChangeShowInCatalog={(show) => setShowBrands(show)}
              showInCatalog={showBrands}
              title={t('pages.globalFiltersPage.brand')}
              titleSelectAll={t('pages.globalFiltersPage.selectBrand')}
            />
          </StyledCol>
        </Row>
      </Col>
      <Col>
        <Subtitle marginTop>{t('pages.globalFiltersPage.subtitleProduct')}</Subtitle>
      </Col>
      <Col>
        <Row>
          <StyledCol col={6} xl={4}>
            <Panel
              items={companies}
              onChangeItems={(items) => handleOnChangeItems(items, setCompanies, companies)}
              title={t('pages.globalFiltersPage.companies')}
              titleSelectAll={t('pages.globalFiltersPage.selectCompanies')}
              withFooter={false}
            />
          </StyledCol>
          <StyledCol col={6} xl={4}>
            <Panel
              items={categories}
              onChangeItems={(items) => handleOnChangeItems(items, setCategories, categories)}
              onChangeShowInCatalog={(show) => setShowCategories(show)}
              showInCatalog={showCategories}
              title={t('pages.globalFiltersPage.categories')}
              titleSelectAll={t('pages.globalFiltersPage.selectCategories')}
            />
          </StyledCol>
          <StyledCol col={6} xl={4}>
            <Panel
              items={productLines}
              onChangeItems={(items) => handleOnChangeItems(items, setProductLines, productLines)}
              onChangeShowInCatalog={(show) => setShowProductLines(show)}
              showInCatalog={showProductLines}
              title={t('pages.globalFiltersPage.productLines')}
              titleSelectAll={t('pages.globalFiltersPage.selectProductLines')}
            />
          </StyledCol>
          <StyledCol col={6} xl={4}>
            <Panel
              items={markets}
              onChangeItems={(items) => handleOnChangeItems(items, setMarkets, markets)}
              onChangeShowInCatalog={(show) => setShowMarkets(show)}
              showInCatalog={showMarkets}
              title={t('pages.globalFiltersPage.market')}
              titleSelectAll={t('pages.globalFiltersPage.selectMarket')}
            />
          </StyledCol>
        </Row>
      </Col>
      <ColSave>
        <ButtonSave
          onClick={handleSave}
          loading={loadingSave}
          loadingLabel={t('common.saving')}
        >
          {t('common.save')}
        </ButtonSave>
      </ColSave>
    </Row>
  );

  return (
    <PageWrapper>
      <PageTitleAndDescription
        title={t('pages.globalFiltersPage.title')}
        description={t('pages.globalFiltersPage.description')}
      />
      {loadingPage ? <Loader withWrapper /> : renderContent()}
    </PageWrapper>
  );
};

export default GlobalFiltersPage;
